import { FC, useCallback, useEffect } from "react";
import { Product, SortOrder } from "tm-shared/types";

import { FilterDrawerUI } from "@/components/Filters/FilterDrawerUI";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useFilters } from "@/hooks/useFilters";
import { useProductsSort } from "@/hooks/useProductsSort";
import {
    patchProductsSelectedFilters,
    setProductsHideZero,
    setProductsSelectedFilters,
    setProductsShowOnlyMML,
    setProductsSortOrder,
    setProductsSortTag,
} from "@/store/slices/ui";

interface OfflineFilterDrawerProps {
    open: boolean;
    setOpen: (value: boolean) => void;
    inventoryList?: {
        [barcode: string]: number;
    };
    mmlList?: Record<string, boolean>;
    setFilteredProducts: (products: Product[]) => void;
}

export const OfflineFilterDrawer: FC<OfflineFilterDrawerProps> = ({
    open,
    setOpen,
    inventoryList = {},
    mmlList,
    setFilteredProducts,
}) => {
    const dispatch = useAppDispatch();

    const { products } = useAppSelector((state) => state.offline);

    const { sort, selectedFilters, showOnlyMML, hideZeroProducts } =
        useAppSelector((s) => s.ui.productsFilter);

    // ✅ Логіка фільтрації тут
    const { filteredProducts, availableFilters } = useFilters({
        products,
        inventoryList,
        filters: selectedFilters,
        hideZeroProducts,
        showOnlyMML,
        mmlList,
    });

    // ✅ Логіка сортування тут
    const { sorted, options, value, order } = useProductsSort({
        items: filteredProducts,
        tag: sort.tag,
        order: sort.order,
        onTagChange: (tag) => dispatch(setProductsSortTag(tag)),
    });

    useEffect(() => {
        setFilteredProducts(sorted);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        order,
        value,
        selectedFilters,
        setFilteredProducts,
        showOnlyMML,
        hideZeroProducts,
    ]);

    const availableProductsLength = sorted.length;

    const onClose = () => setOpen(false);

    const onToggleHideZeroProducts = () => {
        dispatch(setProductsHideZero(!hideZeroProducts));
    };

    const onToggleShowOnlyMML = () => {
        dispatch(setProductsShowOnlyMML(!showOnlyMML));
    };

    const handleFilterChange = useCallback(
        (tag: string, values: (string | number | boolean)[]) => {
            dispatch(patchProductsSelectedFilters({ [tag]: values }));
        },
        [dispatch],
    );

    const handleSortTagChange = useCallback(
        (tag: string | null) => {
            dispatch(setProductsSortTag(tag));
        },
        [dispatch],
    );

    const handleSortOrderChange = useCallback(
        (order: SortOrder) => {
            dispatch(setProductsSortOrder(order));
        },
        [dispatch],
    );

    const handleResetFilters = () => {
        dispatch(setProductsSelectedFilters({}));
        dispatch(setProductsSortOrder(SortOrder.Asc));
        dispatch(setProductsSortTag(null));
        onClose();
    };

    return (
        <FilterDrawerUI
            onClose={onClose}
            onApply={onClose}
            onReset={handleResetFilters}
            isOpen={open}
            products={products}
            availableFilters={availableFilters}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            availableProductsLength={availableProductsLength}
            hideZeroProducts={hideZeroProducts}
            onToggleHideZeroProducts={onToggleHideZeroProducts}
            showOnlyMML={mmlList ? showOnlyMML : undefined}
            onToggleShowOnlyMML={mmlList ? onToggleShowOnlyMML : undefined}
            sortConfig={{
                sortOrder: order,
                sortTag: value,
                sortOptions: options,
                onSortTagChange: handleSortTagChange,
                onSortOrderChange: handleSortOrderChange,
            }}
        />
    );
};
